import config from "@/config";
import type { IProductCard } from "@/types/product";

import usePersistedStore from "../stateManagement/persistedState/persistedStore";

export type ProductCardWishlist = {
  active: boolean;
  toggle: () => void;
};

type SingleVariantCard = {
  id: string;
  wishlist?: ProductCardWishlist;
  [index: string]: unknown;
};

type MultiVariantCard = {
  default: string;
  variants: {
    [index: string]: {
      id?: string;
      wishlist?: ProductCardWishlist;
      [index: string]: unknown;
    };
  };
};

type UseWishlist = typeof useWishlistLocal;
type AddWishlist = (products: IProductCard[]) => IProductCard[];

const variantCardWishlistBuilder =
  <T extends SingleVariantCard[]>(
    includes: (id: string) => boolean,
    toggle: (id: string) => void
  ) =>
  (products: T) =>
    products.map((product) => ({
      ...product,
      wishlist: {
        active: includes(product.id),
        toggle: () => toggle(product.id)
      }
    }));

const productCardWishlistBuilder =
  <T extends MultiVariantCard[]>(
    includes: (id: string) => boolean,
    toggle: (id: string) => void
  ) =>
  (products: T) =>
    products.map((product) => {
      Object.keys(product.variants).forEach((variant) => {
        let v = product.variants[variant];
        if (v && v.id) {
          v.wishlist = {
            active: includes(v.id),
            toggle: () => toggle(v.id ?? "")
          };
        }
      });
      return product;
    });

const useWishlistLocal = (productId?: string | undefined) => {
  const [
    ids,
    removedIds,
    add,
    remove,
    restoreLastRemoved,
    clearAll,
    clearRemoved,
    toggle
  ] = usePersistedStore((store) => [
    store.wishlistIds,
    store.wishlistRemoved,
    store.wishlistAddId,
    store.wishlistRemoveId,
    store.wishlistRestoreLastRemoved,
    store.wishlistClearAll,
    store.wishlistClearRemoved,
    store.wishlistToggleId
  ]);

  const includes = (id: string) => ids.includes(id);

  const addWishlistToVariantCards = variantCardWishlistBuilder(
    includes,
    toggle
  );

  const addWishlistToProductCards = productCardWishlistBuilder(
    includes,
    toggle
  );

  return {
    ids,
    removedIds,
    add: productId ? () => add(productId) : () => {},
    remove: productId ? () => remove(productId) : () => {},
    restoreLastRemoved,
    clearAll,
    clearRemoved,
    toggle: () => (productId ? toggle(productId) : () => {}),
    isIncluded: () => (productId ? includes(productId) : void 0),
    addWishlistToVariantCards,
    addWishlistToProductCards:
      config.variantsPerDisplay === "multi"
        ? (addWishlistToProductCards as unknown as AddWishlist)
        : (addWishlistToVariantCards as unknown as AddWishlist)
  };
};

const useWishlistCentra = () => {
  return {
    ids: [],
    removedIds: [],
    add: () => {},
    remove: () => {},
    restoreLastRemoved: () => {},
    clearAll: () => {},
    clearRemoved: () => {},
    toggle: () => {},
    isIncluded: () => false,
    addWishlistToVariantCards: () => [],
    addWishlistToProductCards: () => []
  };
};

export default config.wishlist === "centra"
  ? (useWishlistCentra as UseWishlist)
  : useWishlistLocal;
